<template>
  <div class="order-items">
    <div v-if="!items || items.length === 0" class="text-muted text-center py-3">
      <i class="fa fa-inbox fa-2x mb-2 d-block"></i>
      {{ $t('no_results') }}
    </div>
    <div v-else class="table-responsive">
      <table class="table table-sm table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>{{ $t('description') }}</th>
            <th>{{ $t('quantityOrdered') }}</th>
            <th>{{ $t('itemCustomerPricePerUnit') }}</th>
            <th>{{ $t('laborCustomerPricePerUnit') }}</th>
            <th>{{ $t('totalCustomerPricePerUnit') }}</th>
            <th>{{ $t('itemCustomerPrice') }}</th>
            <th>{{ $t('laborCustomerPrice') }}</th>
            <th>{{ $t('totalPrice') }}</th>
            <th>{{ $t('actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(orderItem, index) in items" :key="getItemKey(orderItem, index)">
            <td>
              <router-link
                v-if="orderItem.item && orderItem.item.number"
                target="_blank"
                :to="{
                  name: 'ItemEditPage',
                  params: { itemNumber: orderItem.item.number },
                }"
                >{{ orderItem.item.number.toString().padStart(4, '0') }}
                <i class="fa fa-sm fa-external-link"></i>
              </router-link>
            </td>
            <td>{{ getItemDescription(orderItem.item) }}</td>
            <td>
              <div class="quantity-controls d-flex align-items-center">
                <button
                  class="btn btn-sm btn-danger"
                  @click="decreaseQuantity(index)"
                  :disabled="orderItem.quantityOrdered <= 1"
                >
                  <i class="fa fa-minus"></i>
                </button>
                <input
                  v-model.number="orderItem.quantityOrdered"
                  type="number"
                  min="1"
                  class="form-control form-control-sm mx-1 quantity-input"
                  @input="updateQuantity(index, $event.target.value)"
                />
                <button class="btn btn-sm btn-success" @click="increaseQuantity(index)">
                  <i class="fa fa-plus"></i>
                </button>
              </div>
            </td>
            <td>
              <span v-if="getItemCustomerPrice(orderItem.item)">
                €{{ getItemCustomerPrice(orderItem.item).toFixed(2) }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span v-if="getLaborCustomerPrice(orderItem.item)">
                €{{ getLaborCustomerPrice(orderItem.item).toFixed(2) }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <strong v-if="getTotalCustomerPrice(orderItem.item)">
                €{{ getTotalCustomerPrice(orderItem.item).toFixed(2) }}
              </strong>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <strong v-if="getItemCustomerPrice(orderItem.item)">
                €{{ getItemCustomerPriceTotal(orderItem).toFixed(2) }}
              </strong>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <strong v-if="getLaborCustomerPrice(orderItem.item)">
                €{{ getLaborCustomerPriceTotal(orderItem).toFixed(2) }}
              </strong>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <strong v-if="getTotalCustomerPrice(orderItem.item)">
                €{{ getTotalCustomerPriceTotal(orderItem).toFixed(2) }}
              </strong>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <button
                class="btn btn-sm btn-danger"
                @click="removeItem(index)"
                :title="$t('remove')"
              >
                <i class="fa fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
        <tfoot v-if="items.length > 0 && hasValidPrices" class="totals-footer">
          <tr>
            <th colspan="6" class="text-right"></th>
            <th>
              <strong>€{{ totalItemCustomerPrice.toFixed(2) }}</strong>
            </th>
            <th>
              <strong>€{{ totalLaborCustomerPrice.toFixed(2) }}</strong>
            </th>
            <th>
              <strong>€{{ totalOrderValue.toFixed(2) }}</strong>
            </th>
            <th></th>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OrderItems',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    totalItemCustomerPrice() {
      return this.items.reduce((total, orderItem) => {
        const itemCustomerPrice = this.getItemCustomerPrice(orderItem.item);
        if (itemCustomerPrice) {
          const quantity = parseInt(orderItem.quantityOrdered, 10) || 0;
          return total + itemCustomerPrice * quantity;
        }
        return total;
      }, 0);
    },
    totalLaborCustomerPrice() {
      return this.items.reduce((total, orderItem) => {
        const laborCustomerPrice = this.getLaborCustomerPrice(orderItem.item);
        if (laborCustomerPrice) {
          const quantity = parseInt(orderItem.quantityOrdered, 10) || 0;
          return total + laborCustomerPrice * quantity;
        }
        return total;
      }, 0);
    },
    totalOrderValue() {
      return this.items.reduce((total, orderItem) => {
        const totalCustomerPrice = this.getTotalCustomerPrice(orderItem.item);
        if (totalCustomerPrice) {
          const quantity = parseInt(orderItem.quantityOrdered, 10) || 0;
          return total + totalCustomerPrice * quantity;
        }
        return total;
      }, 0);
    },
    hasValidPrices() {
      return this.items.some((item) => this.getTotalCustomerPrice(item.item));
    },
  },
  methods: {
    getItemKey(orderItem, index) {
      // Use item ID if available, otherwise fall back to index
      return orderItem.item?._id || orderItem.item || index;
    },
    getItemNumber(item) {
      if (typeof item === 'object' && item.number) {
        return item.number.toString().padStart(4, '0');
      }
      return '-';
    },
    getItemDescription(item) {
      if (typeof item === 'object' && item.description) {
        return item.description;
      }
      return '-';
    },
    getItemCustomerPrice(item) {
      if (item && item.priceCalculation && item.priceCalculation.itemCustomerPrice) {
        return parseFloat(item.priceCalculation.itemCustomerPrice);
      }
      return 0;
    },
    getLaborCustomerPrice(item) {
      if (item && item.priceCalculation && item.priceCalculation.laborCustomerPrice) {
        return parseFloat(item.priceCalculation.laborCustomerPrice);
      }
      return 0;
    },
    getTotalCustomerPrice(item) {
      if (item && item.priceCalculation && item.priceCalculation.totalCustomerPrice) {
        return parseFloat(item.priceCalculation.totalCustomerPrice);
      }
      return 0;
    },
    getItemCustomerPriceTotal(orderItem) {
      const itemCustomerPrice = this.getItemCustomerPrice(orderItem.item);
      if (!itemCustomerPrice) return 0;

      const quantityOrdered = parseInt(orderItem.quantityOrdered, 10) || 0;
      return itemCustomerPrice * quantityOrdered;
    },
    getLaborCustomerPriceTotal(orderItem) {
      const laborCustomerPrice = this.getLaborCustomerPrice(orderItem.item);
      if (!laborCustomerPrice) return 0;

      const quantityOrdered = parseInt(orderItem.quantityOrdered, 10) || 0;
      return laborCustomerPrice * quantityOrdered;
    },
    getTotalCustomerPriceTotal(orderItem) {
      const totalCustomerPrice = this.getTotalCustomerPrice(orderItem.item);
      if (!totalCustomerPrice) return 0;

      const quantityOrdered = parseInt(orderItem.quantityOrdered, 10) || 0;
      return totalCustomerPrice * quantityOrdered;
    },
    increaseQuantity(index) {
      const updatedItems = [...this.items];
      const currentQuantity = parseInt(updatedItems[index].quantityOrdered || 0, 10);
      updatedItems[index] = {
        ...updatedItems[index],
        quantityOrdered: currentQuantity + 1,
      };
      this.$emit('update-items', updatedItems);
    },
    decreaseQuantity(index) {
      const currentQuantity = parseInt(this.items[index].quantityOrdered || 0, 10);
      if (currentQuantity > 1) {
        const updatedItems = [...this.items];
        updatedItems[index] = {
          ...updatedItems[index],
          quantityOrdered: currentQuantity - 1,
        };
        this.$emit('update-items', updatedItems);
      }
    },
    updateQuantity(index, newQuantity) {
      const quantity = parseInt(newQuantity) || 1;
      if (quantity >= 1) {
        const updatedItems = [...this.items];
        updatedItems[index] = {
          ...updatedItems[index],
          quantityOrdered: quantity,
        };
        this.$emit('update-items', updatedItems);
      }
    },
    removeItem(index) {
      const updatedItems = [...this.items];
      updatedItems.splice(index, 1);
      this.$emit('update-items', updatedItems);
    },
  },
};
</script>

<style scoped lang="scss">
.order-items {
  .quantity-controls {
    width: 140px;

    .quantity-input {
      width: 75px;
      text-align: right;
    }

    .btn {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    }
  }

  .table {
    margin-bottom: 0;

    th,
    td {
      vertical-align: middle;
    }

    .btn-sm {
      padding: 0.25rem 0.5rem;
      font-size: 0.875rem;
    }

    .totals-footer {
      border-top: 1px solid #dee2e6;
    }
  }
}
</style>
